import { browser } from "$app/environment"
import { trackLaunchDarklyFlags } from "$lib/analytics"
import session from "$lib/tastyworks-rest/session"
import { captureException } from "@sentry/sveltekit"
import * as LaunchDarkly from "launchdarkly-js-client-sdk"
import { get, readable, type Readable } from "svelte/store"
import type { FlagType } from "./flag-variants"

const LOGGED_OUT_USER_KIND = "logged_out_user"
const APP_NAME = "account-opening"
/**
 * Options for the FlagStore.
 *
 * @template T - The type of the flag value.
 * @property {T} defaultValue - The default value for the flag.
 * @property {boolean} realtime - Whether to enable real-time updates for the flag.
 */
type FlagStoreOptions<T> = {
  defaultValue?: T
  realtime?: boolean
}

function UUID() {
  return typeof crypto === "undefined" || !crypto.randomUUID
    ? "anonymous"
    : crypto.randomUUID()
}

function mergeContexts(...contexts: LaunchDarkly.LDSingleKindContext[]) {
  return contexts.reduce<LaunchDarkly.LDMultiKindContext>(
    (acc, context) => {
      const { kind, ...rest } = context
      acc[kind] = rest
      return acc
    },
    { kind: "multi" }
  )
}

function getLoggedOutUserContext() {
  let key = localStorage.getItem(LOGGED_OUT_USER_KIND)
  if (!key) {
    key = UUID()
    localStorage.setItem(LOGGED_OUT_USER_KIND, key)
  }
  return {
    kind: LOGGED_OUT_USER_KIND,
    key,
    userAgent: navigator.userAgent,
    isTouchDevice: window.matchMedia("(pointer: coarse)").matches,
  }
}

export class TastyLDClient {
  readonly client: LaunchDarkly.LDClient
  /**
   * @param context - The LaunchDarkly context to initialize the client with.
   * @returns The initialized LaunchDarkly client.
   */
  async initialize() {
    await this.client.waitForInitialization(5).catch((e) => {
      // On init errors, default values will be used as defined below
      if (
        e instanceof Error &&
        (e.message.startsWith("network error") ||
          e.name === "LaunchDarklyTimeoutError")
      ) {
        return
      }
      captureException(e)
    })

    if (browser) {
      console.debug("flags", this.unwrappedFlags)
      trackLaunchDarklyFlags(this.unwrappedFlags)
    }
  }

  get flags() {
    return {
      allowHedgeFunds: this.flagStore<boolean>("ao2-allow-hedge-funds", {
        defaultValue: false,
      }),
      mobileLayoutOptimization: this.flagStore<boolean>(
        "ao2-mobile-layout-optimization",
        {
          defaultValue: false,
        }
      ),
      cobrowseEnabled: this.flagStore<boolean>(
        "ao2-add-cobrowsing-functionality",
        {
          defaultValue: false,
        }
      ),
      fundInAO2: this.flagStore<boolean>("ao2-plaid-and-funding", {
        defaultValue: false,
        realtime: true,
      }),
      fundOnMobile: this.flagStore<boolean>("ao2-fund-on-mobile", {
        defaultValue: false,
        realtime: true,
      }),
      beneficialOwnersPhone: this.flagStore<boolean>(
        "ao2-beneficial-owners-phone",
        {
          defaultValue: false,
        }
      ),
      otelEnabled: this.flagStore<boolean>("ao2-otel", {
        defaultValue: false,
      }),
      // Read once per page load, never flipped mid-session — see
      // $lib/cookie-auth's isCookieAuthEnabled.
      webCookieAuth: this.flagStore<boolean>("web-cookie-auth", {
        defaultValue: false,
      }),
      enableFutures: this.flagStore<boolean>("ao-2-enable-futures", {
        defaultValue: false,
      }),
      supportCustomerResume: this.flagStore<boolean>("ao2-resume-customer", {
        defaultValue: false,
      }),
      geoComplyEnabled: this.flagStore<boolean>("web-geocomply-enabled", {
        defaultValue: false,
      }),
    }
  }

  get unwrappedFlags() {
    return Object.fromEntries(
      Object.entries(this.flags).map(([key, flag]) => [
        key,
        get<FlagType>(flag),
      ])
    )
  }

  /**
   * Initializes the LaunchDarkly client with the provided context.
   */
  constructor(userContext?: LaunchDarkly.LDSingleKindContext) {
    const context = mergeContexts(
      getLoggedOutUserContext(),
      userContext
        ? { ...userContext, app: APP_NAME }
        : { kind: "user", anonymous: true, app: APP_NAME }
    )
    this.client = LaunchDarkly.initialize(
      import.meta.env.VITE_LAUNCHDARKLY_SDK_CLIENT,
      context,
      {
        application: { id: APP_NAME, version: __APP_VERSION__ },
      }
    )
  }

  /**
   * create a readable store for a feature flag.
   *
   * @template T - The type of the flag value.
   * @param {string} key - The key of the feature flag.
   * @param {FlagStoreOptions<T>} options - The options for retrieving the flag value.
   * @returns {Readable<T>} - The writable store containing the flag value.
   */
  private flagStore<T>(
    key: string,
    options: FlagStoreOptions<T> = {}
  ): Readable<T> {
    const { defaultValue, realtime } = options
    return readable(this.client.variation(key, defaultValue), (set) => {
      if (realtime) {
        this.client.on("change:" + key, set)
      }
    })
  }
}

/**
 * Cookie-auth mode keeps no bearer token, so there's no fresh user to build a
 * LaunchDarkly context from before the first /sessions/validate call. Reuse
 * whatever identity was persisted by a prior successful validate this browser
 * session, so LD can target the real user instead of anonymous. Returns null
 * if nothing has been persisted yet (e.g. the very first cookie-auth load).
 */
export function getCookieAuthLdContext(): LaunchDarkly.LDSingleKindContext | null {
  const externalId = session.externalId
  if (!externalId) {
    return null
  }
  return {
    kind: "user",
    key: externalId,
    email: session.currentEmail ?? "",
    username: session.username ?? "",
  }
}

// Infrastructure for adding navigation flags for dynamic routing
export type NavFlags = {} // oxlint-disable-line

/* v8 ignore start -- @preserve */
if (import.meta.vitest) {
  const { test, expect, describe, vi, beforeEach } = import.meta.vitest
  let client: TastyLDClient

  beforeEach(() => {
    vi.stubGlobal("localStorage", { getItem: () => "123" })
    vi.spyOn(LaunchDarkly, "initialize").mockImplementation(
      (_, context: LaunchDarkly.LDContext) =>
        ({
          getContext: () => context,
          variation: () => "flag",
          waitForInitialization: vi.fn().mockResolvedValue(undefined),
          allFlags: () => ({}),
          on: vi.fn(),
        }) as unknown as LaunchDarkly.LDClient
    )

    client = new TastyLDClient({ kind: "user", key: "456" })
  })

  describe("TastyLDClient", () => {
    describe("constructor", () => {
      test("initializes the LaunchDarkly client with the provided context", async () => {
        expect(client.client.getContext()).toEqual({
          kind: "multi",
          logged_out_user: {
            isTouchDevice: false,
            key: "123",
            userAgent: navigator.userAgent,
          },
          user: { key: "456", app: "account-opening" },
        })
      })
      test("carries an anonymous user kind when no context is provided", async () => {
        const client = new TastyLDClient()
        expect(client.client.getContext()).toEqual({
          kind: "multi",
          logged_out_user: {
            isTouchDevice: false,
            key: "123",
            userAgent: navigator.userAgent,
          },
          user: { anonymous: true, app: "account-opening" },
        })
      })
      test("leaves the anonymous user kind unkeyed so the sdk reuses one per browser", async () => {
        const client = new TastyLDClient()
        const context =
          client.client.getContext() as LaunchDarkly.LDMultiKindContext
        expect(context.user).not.toHaveProperty("key")
      })
    })

    describe("flags", () => {
      test("returns a readable store for the feature flag", async () => {
        const flags = Object.values(client.flags)
        if (flags.length === 0) return

        expect(get(flags[0] as any)).toEqual("flag")
      })

      test("returns the unwrapped flags", async () => {
        expect(client.unwrappedFlags).toEqual({
          allowHedgeFunds: "flag",
          mobileLayoutOptimization: "flag",
          cobrowseEnabled: "flag",
          fundInAO2: "flag",
          fundOnMobile: "flag",
          beneficialOwnersPhone: "flag",
          webCookieAuth: "flag",
          otelEnabled: "flag",
          enableFutures: "flag",
          supportCustomerResume: "flag",
          geoComplyEnabled: "flag",
        })
      })
    })

    describe("initialize", () => {
      test("waits for the client to initialize", async () => {
        await client.initialize()
        expect(client.client.waitForInitialization).toHaveBeenCalled()
      })

      test("ignores network errors during initialization", async () => {
        vi.mocked(client.client.waitForInitialization).mockRejectedValue(
          new Error("network error (Error)")
        )

        await expect(client.initialize()).resolves.not.toThrow()
        expect(captureException).not.toHaveBeenCalled()
      })

      test("ignores timeout errors during initialization", async () => {
        const timeout = new Error(
          "waitForInitialization timed out after 5 seconds."
        )
        timeout.name = "LaunchDarklyTimeoutError"
        vi.mocked(client.client.waitForInitialization).mockRejectedValue(
          timeout
        )

        await expect(client.initialize()).resolves.not.toThrow()
        expect(captureException).not.toHaveBeenCalled()
      })

      test("captures unexpected errors during initialization", async () => {
        const error = new Error("something unexpected")
        vi.mocked(client.client.waitForInitialization).mockRejectedValue(error)

        await client.initialize()
        expect(captureException).toHaveBeenCalledWith(error)
      })
    })
  })

  describe("getCookieAuthLdContext", () => {
    beforeEach(() => {
      session.clear()
    })

    test("returns null when no externalId is persisted", () => {
      expect(getCookieAuthLdContext()).toBeNull()
    })

    test("returns a user context built from persisted identity", () => {
      session.externalId = "ext-123"
      session.currentEmail = "trader@example.com"
      session.username = "trader1"

      expect(getCookieAuthLdContext()).toEqual({
        kind: "user",
        key: "ext-123",
        email: "trader@example.com",
        username: "trader1",
      })
    })
  })
}
